import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import {
  BusinessOutlined,
  QrCodeScannerOutlined,
  RefreshOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import useApiEp from "../hooks/useApiEp";

const ExhibitorsScannedAnalytics = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState("count");
  const [order, setOrder] = useState("desc");

  const {
    data: exhibitors = [],
    isLoading: loadingExhibitors,
    isFetching: fetchingExhibitors,
    isError: exhibitorsError,
    refetch: refetchExhibitors,
  } = useApiEp("/admin/get-exhibitors-lookup-mini");

  const {
    data: scanned = [],
    isLoading: loadingScanned,
    isFetching: fetchingScanned,
    isError: scannedError,
    refetch: refetchScanned,
  } = useApiEp("/admin/get-exhibitors-scanned-wise");

  const isLoading = loadingExhibitors || loadingScanned;
  const isFetching = fetchingExhibitors || fetchingScanned;
  const isError = exhibitorsError || scannedError;

  const rows = useMemo(() => {
    const companyLookup = new Map(
      (Array.isArray(exhibitors) ? exhibitors : []).map((exhibitor) => {
        const companyName =
          typeof exhibitor.companyName === "string"
            ? exhibitor.companyName
            : exhibitor.companyName?.value;

        return [
          exhibitor.username,
          typeof companyName === "string" ? companyName.trim() : "",
        ];
      })
    );

    return (Array.isArray(scanned) ? scanned : []).map((item) => {
      const count = Number(item.count);

      return {
        username: item.username || "",
        companyName: companyLookup.get(item.username) || "Unknown company",
        count: Number.isFinite(count) ? count : 0,
      };
    });
  }, [exhibitors, scanned]);

  const totalLeads = useMemo(
    () => rows.reduce((total, row) => total + row.count, 0),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();

    return rows
      .filter(
        (row) =>
          !query ||
          row.companyName.toLowerCase().includes(query) ||
          String(row.username).toLowerCase().includes(query)
      )
      .sort((a, b) => {
        const comparison =
          orderBy === "count"
            ? a.count - b.count
            : a.companyName.localeCompare(b.companyName, undefined, {
                sensitivity: "base",
              });

        return (
          (order === "asc" ? comparison : -comparison) ||
          String(a.username).localeCompare(String(b.username))
        );
      });
  }, [rows, search, order, orderBy]);

  // Keep pagination valid if refreshed results contain fewer records.
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1)
  );

  const visibleRows = filteredRows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const handleSort = (column) => {
    setOrder(
      orderBy === column
        ? order === "asc"
          ? "desc"
          : "asc"
        : column === "count"
        ? "desc"
        : "asc"
    );
    setOrderBy(column);
    setPage(0);
  };

  const handleRefresh = () => {
    refetchExhibitors();
    refetchScanned();
  };

  const formatNumber = (value) => value.toLocaleString("en-IN");

  return (
    <Box
      sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: { xs: 2, md: 3 } }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "-0.5px" }}
          >
            Exhibitor Leads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Track the number of leads scanned by each exhibitor.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={
            isFetching ? <CircularProgress size={16} /> : <RefreshOutlined />
          }
          onClick={handleRefresh}
          disabled={isFetching}
          sx={{ borderRadius: 2, textTransform: "none", flexShrink: 0 }}
        >
          Refresh
        </Button>
      </Stack>

      {isError ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRefresh}
              disabled={isFetching}
            >
              Retry
            </Button>
          }
        >
          Unable to load exhibitor leads. Please try again.
        </Alert>
      ) : isLoading ? (
        <Paper
          variant="outlined"
          sx={{ p: 8, textAlign: "center", borderRadius: 3 }}
        >
          <CircularProgress size={32} />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Loading exhibitor leads…
          </Typography>
        </Paper>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            {[
              {
                label: "Total Leads Scanned",
                value: totalLeads,
                icon: <QrCodeScannerOutlined />,
                color: "#2563eb",
                background: "#eff6ff",
              },
              {
                label: "Exhibitors with Scans",
                value: rows.filter((row) => row.count > 0).length,
                icon: <BusinessOutlined />,
                color: "#059669",
                background: "#ecfdf5",
              },
            ].map((stat) => (
              <Paper
                key={stat.label}
                variant="outlined"
                sx={{
                  flex: 1,
                  p: 2.5,
                  borderRadius: 3,
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: stat.background,
                      color: stat.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.25 }}>
                      {formatNumber(stat.value)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Paper
            variant="outlined"
            sx={{ borderRadius: 3, overflow: "hidden", borderColor: "divider" }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ p: 2.5 }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  Leads by Exhibitor
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(filteredRows.length)} exhibitor
                  {filteredRows.length === 1 ? "" : "s"}
                  {search.trim() ? " matching your search" : ""}
                </Typography>
              </Box>

              <TextField
                size="small"
                placeholder="Search company or username"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                inputProps={{ "aria-label": "Search company or username" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 320 },
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />
            </Stack>

            <TableContainer>
              <Table
                sx={{ minWidth: 480 }}
                aria-label="Exhibitor leads scanned"
              >
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: "grey.50",
                      "& th": {
                        fontWeight: 700,
                        color: "text.secondary",
                        borderTop: "1px solid",
                        borderTopColor: "divider",
                      },
                    }}
                  >
                    <TableCell sx={{ width: 70, pl: 3 }}>#</TableCell>
                    <TableCell
                      sortDirection={orderBy === "companyName" ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === "companyName"}
                        direction={orderBy === "companyName" ? order : "asc"}
                        onClick={() => handleSort("companyName")}
                      >
                        Company Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell
                      align="right"
                      sortDirection={orderBy === "count" ? order : false}
                      sx={{ pr: 3, whiteSpace: "nowrap" }}
                    >
                      <TableSortLabel
                        active={orderBy === "count"}
                        direction={orderBy === "count" ? order : "desc"}
                        onClick={() => handleSort("count")}
                      >
                        Leads Scanned
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 7 }}>
                        <SearchOutlined
                          sx={{ fontSize: 36, color: "text.disabled", mb: 1 }}
                        />
                        <Typography sx={{ fontWeight: 600 }}>
                          {search.trim()
                            ? "No matching exhibitors"
                            : "No leads scanned yet"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {search.trim()
                            ? "Try another company name or username."
                            : "Exhibitors will appear here once they scan leads."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map((row, index) => (
                      <TableRow
                        key={row.username || "__unknown__"}
                        hover
                        sx={{ "&:last-child td": { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ pl: 3, color: "text.secondary" }}>
                          {currentPage * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.companyName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere" }}
                          >
                            {row.username || "Username unavailable"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 3 }}>
                          <Chip
                            label={formatNumber(row.count)}
                            size="small"
                            sx={{
                              minWidth: 56,
                              height: 30,
                              fontWeight: 700,
                              bgcolor: "#eff6ff",
                              color: "#1d4ed8",
                              borderRadius: 1.5,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredRows.length}
              page={currentPage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ExhibitorsScannedAnalytics;
